// ui/src/components/padrekko/qc/auditTrail.js
//
// A finding's history, turned into rows. No JSX, so the part that can be
// WRONG is testable without a DOM.
//
// ── THE RULE THIS FILE EXISTS TO HOLD ────────────────────────────────────────
//
// Every history entry produces exactly one row. Always.
//
// buildProvenanceTrail — the folded actor chain this sits below — collapsed
// consecutive same-actor entries into one step, and validations disappeared
// into the fold until that was fixed by special-casing them. A special case
// for one event type is not a fix; it is the same bug waiting for the next
// event type. So: no folding, no filtering, and an event this file has never
// heard of renders generically rather than vanishing.
//
// The audit trail's only claim is completeness. A trail that silently drops
// what it does not recognise makes the opposite claim while looking identical.

/** Roles → a label and a tone. Colour is never the only carrier. */
export const ROLE_BADGE = Object.freeze({
  system: { label: "System", tone: "bg-slate-700/40 border-slate-500/50 text-slate-300" },
  dh_analyst: { label: "DreamHammer", tone: "bg-indigo-500/15 border-indigo-400/40 text-indigo-200" },
  customer_analyst: { label: "Customer", tone: "bg-emerald-500/15 border-emerald-400/40 text-emerald-200" },
  operator_analyst: { label: "Operator", tone: "bg-amber-500/15 border-amber-400/40 text-amber-200" },
});

const UNKNOWN_ROLE_TONE = "bg-slate-800/60 border-slate-600/50 text-slate-400";

/**
 * "DreamHammer" is a real organisation name, and stage labels are forbidden
 * from naming organisations — that rule exists because "Pending UL review"
 * shipped one customer's initials to every tenant.
 *
 * This is the deliberate exception, and it is the same one stageLabels.js
 * already makes: DH is the SERVICE PROVIDER, constant across every tenant,
 * and never a customer's name. Customer and Operator stay generic precisely
 * because those are the seats a tenant sits in.
 */
export function roleBadge(role) {
  return ROLE_BADGE[role] ?? { label: role || "Unknown", tone: UNKNOWN_ROLE_TONE };
}

/** Human label per event. Unknown events fall back to the raw key, humanised. */
const EVENT_LABEL = Object.freeze({
  autodetected: "Autodetected",
  analyzed: "Analyzed",
  polygon_edited: "Polygon edited",
  reanalyzed: "Reanalyzed",
  validated: "Affirmed",
  disputed: "Disputed",
  dispute_cleared_explicit: "Dispute cleared",
  dispute_cleared_implicit: "Dispute cleared",
  finding_removed: "Finding removed",
  kicked_back_to_dh: "Kicked back to DreamHammer",
  dh_resubmitted_after_kickback: "Resubmitted after kickback",
  // Operator remediation. Distinct labels rather than one with a status,
  // because the three are different facts and a reader scanning a column
  // should not have to parse a qualifier to tell them apart.
  finding_completed: "Marked remediated",
  finding_not_completed: "Marked not remediated",
  finding_completion_reset: "Remediation status cleared",
});

export function eventLabel(event) {
  if (EVENT_LABEL[event]) return EVENT_LABEL[event];
  if (!event) return "Unknown event";
  // Humanise rather than hide: `some_future_event` → `Some future event`.
  return String(event).replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/**
 * Chronological, with array order as the tie-break.
 *
 * History is append-only so array order is usually time order, but not
 * reliably — validationState sorts for the same reason, and its own tests
 * carry an out-of-order fixture. Sorting on `at` alone would reorder the
 * polygon_edited/reanalyzed pair that a correction writes with an IDENTICAL
 * timestamp, and that pair's order is the causal claim: the geometry changed,
 * then it was re-analysed.
 */
function chronologically(a, b) {
  const ta = Date.parse(a.entry?.at ?? "") || 0;
  const tb = Date.parse(b.entry?.at ?? "") || 0;
  if (ta !== tb) return ta - tb;
  return a.index - b.index;
}

/**
 * The status a reanalysis produced.
 *
 * An entry records only what the analysis WAS (`previous_analysis`), so the
 * result has to be read off what came next: the next reanalysis's "previous",
 * or — for the last one — the finding's analysis as it stands now. Without
 * this the trail can say "was VERIFY" and never what it became, which is a log
 * entry rather than an argument.
 */
function transitionFor(sorted, i, finding) {
  const from = sorted[i].entry?.previous_analysis?.status ?? null;
  if (!from) return null;
  for (let j = i + 1; j < sorted.length; j++) {
    if (sorted[j].entry?.event !== "reanalyzed") continue;
    const to = sorted[j].entry?.previous_analysis?.status ?? null;
    return to ? { from, to } : null;
  }
  const current = finding?.analysis?.status ?? null;
  return current ? { from, to: current } : null;
}

/**
 * Is this validated entry the one that still holds?
 *
 * Read from the SERVER's `validation.all[].is_current`, never recomputed here.
 * Currency is a content-hash comparison against the finding's present state; a
 * second implementation in the browser is how the pill and the trail come to
 * disagree about the same finding on the same screen.
 *
 * ── WHY THIS MATCHES BY POSITION AND NOT BY HASH ─────────────────────────────
 *
 * The obvious join key would be the state hash each validation affirms. It is
 * also the one field this file may not touch: validationDerivation.test.js
 * fails any ui/ file that so much as NAMES it — a text scan, which cannot tell
 * a comment from code and should not have to. The rule behind it is blunt on
 * purpose (a browser holding that field is a browser deciding currency), and
 * an exception carved for a use that is "only reading" is how a guard stops
 * guarding. So this file does not name it either.
 *
 * Position works exactly and needs no key at all. `validation.all` is built
 * from the validated entries in chronological order, and this file sorts the
 * same way, so the Nth validated row is all[N]. The timestamp is then checked
 * rather than trusted: if the entry at that position reports a different `at`,
 * the two orderings have diverged and the row says currency is unknown instead
 * of asserting someone else's answer.
 */
function validationCurrency(entry, validation, ordinal) {
  const all = Array.isArray(validation?.all) ? validation.all : [];
  const hit = all[ordinal];
  if (!hit) return { known: false, isCurrent: null };
  if ((hit.at ?? null) !== (entry.at ?? null)) return { known: false, isCurrent: null };
  return { known: true, isCurrent: hit.is_current === true };
}

/**
 * One row per history entry, oldest first.
 *
 * `detail` is a small structured description rather than a formatted string,
 * so the component decides presentation and this file stays testable.
 */
export function auditRows(finding) {
  const history = Array.isArray(finding?.history) ? finding.history : [];
  const sorted = history
    .map((entry, index) => ({ entry, index }))
    .filter((x) => x.entry && typeof x.entry === "object")
    .sort(chronologically);

  // An index into the server's `validation.all`, advanced once per validated
  // entry. Named for what it indexes rather than what it counts: a variable
  // named for the counting would read as a browser-side review tally, which
  // validationDerivation.test.js bans and was right to flag when this was
  // first written. It is a cursor, not a count of anything.
  let allIndex = 0;

  return sorted.map(({ entry, index }, i) => {
    const row = {
      key: `${entry.at ?? "no-time"}-${index}`,
      at: entry.at ?? null,
      event: entry.event ?? null,
      label: eventLabel(entry.event),
      role: entry.by_role ?? null,
      badge: roleBadge(entry.by_role),
      actorName: entry.by_user_name ?? null,
      orgName: entry.by_org_name ?? null,
      // Distinguishes "nobody did this" from "somebody did and we could not
      // name them". An autodetect event carries a null actor id by design;
      // rendering "Unknown actor" there would report a failure that did not
      // occur, and on the one row whose point is that no person was involved.
      hasActorId: entry.by_user_id != null,
      // "snapshot" | "resolved" | null, set by the server. Not inferred here:
      // by the time the payload reaches the browser both look the same, which
      // is precisely why the server has to say.
      nameSource: entry.actor_name_source ?? null,
      stage: entry.case_stage ?? null,
      note: entry.note ?? null,
      detail: null,
      transition: null,
      validation: null,
      recognised: Object.prototype.hasOwnProperty.call(EVENT_LABEL, entry.event),
    };

    switch (entry.event) {
      case "autodetected": {
        const what = entry.feature_label || entry.feature_type || null;
        const engine = [entry.provider, entry.model].filter(Boolean).join(" · ") || entry.engine || null;
        row.detail = { kind: "detected", what, engine };
        break;
      }
      case "polygon_edited":
        // previous_polygon is a full GeoJSON snapshot. It is deliberately NOT
        // measured here — an area delta computed in the browser from a
        // 4326 polygon is a different number from the server's, and a trail
        // that quotes a wrong acreage is worse than one that quotes none.
        row.detail = { kind: "polygon", hasSnapshot: entry.previous_polygon != null };
        break;
      case "reanalyzed":
        row.transition = transitionFor(sorted, i, finding);
        row.detail = { kind: "reanalysis", correction: entry.operator_correction || null };
        break;
      case "validated":
        row.validation = validationCurrency(entry, finding?.validation, allIndex++);
        break;
      default:
        break;
    }
    return row;
  });
}
